import { ReactNode, useState } from 'react';
import { Check, Copy, RefreshCw, ThumbsDown, ThumbsUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Tooltip, TooltipContent, TooltipTrigger } from '@Components/ui-v2/tooltip';
import { TrackedButton } from '@Components/ui-v2/tracked-button';
import { useFeedback } from '@CustomHooks';
import { FeedbackReason, FeedbackSignal, FeedbackType } from '@Models';
import { markActedOnLastAnswer } from '../../../Helpers/Agents/lastAnswer';
import { sendSignal } from '../../../Helpers/Agents/signals';
import FeedbackModal, { type FeedbackModalMode } from './FeedbackModal';

const VOTE_BY_MODE: Record<FeedbackModalMode, FeedbackType> = {
  positive: FeedbackType.POSITIVE,
  negative: FeedbackType.NEGATIVE,
};

interface MessageActionsProps {
  content: string;
  timestamp: Date;
  conversationId: string;
  interactionTimestamp: string;
  feedback?: FeedbackType;
  /** True for the last message in the list: shows the task question. */
  isLatest: boolean;
  onRegenerate?: () => void;
}

const formatTime = (date: Date): string => date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

const ActionButton = ({
  label,
  buttonName,
  onClick,
  active,
  disabled = false,
  children,
}: {
  label: string;
  buttonName: string;
  onClick?: () => void;
  /** Only toggles pass this: it turns the button into an aria toggle. */
  active?: boolean;
  disabled?: boolean;
  children: ReactNode;
}) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <TrackedButton
        type="button"
        variant="ghost"
        buttonName={buttonName}
        aria-label={label}
        aria-pressed={active}
        onClick={onClick}
        disabled={disabled}
        // `[&_svg]:size-[14px]` overrides the `size-4` baked into buttonVariants.
        className={`flex h-7 w-7 items-center justify-center rounded-md p-0 cursor-pointer transition-colors duration-200 [&_svg]:size-[14px] disabled:cursor-not-allowed disabled:opacity-50 ${
          active
            ? 'bg-[#7c6cf6]/10 text-[#7c6cf6] hover:bg-[#7c6cf6]/15 hover:text-[#7c6cf6]'
            : 'text-gray-400 hover:bg-[#7c6cf6]/10 hover:text-[#7c6cf6]'
        }`}>
        {children}
      </TrackedButton>
    </TooltipTrigger>
    <TooltipContent side="bottom" className="text-xs">
      {label}
    </TooltipContent>
  </Tooltip>
);

const MessageActions = ({
  content,
  timestamp,
  conversationId,
  interactionTimestamp,
  feedback,
  isLatest,
  onRegenerate,
}: MessageActionsProps) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  // `mode` outlives `open` so the exit animation keeps painting the right chips.
  const [modal, setModal] = useState<{ mode: FeedbackModalMode; open: boolean }>({
    mode: 'negative',
    open: false,
  });
  const { updateFeedback, isUpdating } = useFeedback();

  const canVote = Boolean(conversationId && interactionTimestamp) && !isUpdating;
  // Not `=== NEUTRAL`: the attribute is absent on items written before it was defaulted.
  const hasVoted = feedback === FeedbackType.POSITIVE || feedback === FeedbackType.NEGATIVE;
  const voteBase = { conversationId, timestamp: interactionTimestamp };

  const handleCopy = () => {
    if (!navigator.clipboard?.writeText) return;
    navigator.clipboard
      .writeText(content)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1200);
        sendSignal(conversationId, interactionTimestamp, FeedbackSignal.COPY);
        markActedOnLastAnswer(conversationId, interactionTimestamp);
      })
      .catch(() => {
        /* clipboard unavailable */
      });
  };

  const handleVoteClick = (mode: FeedbackModalMode) => {
    if (!canVote) return;
    markActedOnLastAnswer(conversationId, interactionTimestamp);
    if (feedback === VOTE_BY_MODE[mode]) {
      // Back to neutral; the backend clears the stored reason and comment.
      updateFeedback({ ...voteBase, feedback: FeedbackType.NEUTRAL });
      return;
    }
    setModal({ mode, open: true });
  };

  const closeModal = () => setModal((current) => ({ ...current, open: false }));

  const handleModalSubmit = (reason: FeedbackReason | null, comment: string) => {
    if (!canVote) return;
    updateFeedback({
      ...voteBase,
      feedback: VOTE_BY_MODE[modal.mode],
      feedbackReason: reason,
      feedbackComment: comment,
    });
    closeModal();
  };

  return (
    <>
      <div className="mt-2 flex flex-wrap items-center gap-1">
        {isLatest && !hasVoted && <span className="mr-1 text-xs text-gray-500">{t('Agent.FeedbackQuestion')}</span>}
        <ActionButton
          label={t('Agent.FeedbackHelpful')}
          buttonName="AGENT_MESSAGE_FEEDBACK_POSITIVE"
          active={feedback === FeedbackType.POSITIVE}
          disabled={!canVote}
          onClick={() => handleVoteClick('positive')}>
          <ThumbsUp strokeWidth={1.75} />
        </ActionButton>
        <ActionButton
          label={t('Agent.FeedbackNotHelpful')}
          buttonName="AGENT_MESSAGE_FEEDBACK_NEGATIVE"
          active={feedback === FeedbackType.NEGATIVE}
          disabled={!canVote}
          onClick={() => handleVoteClick('negative')}>
          <ThumbsDown strokeWidth={1.75} />
        </ActionButton>
        <ActionButton
          label={copied ? t('Agent.Copied') : t('Agent.CopyResponse')}
          buttonName="AGENT_MESSAGE_COPY"
          onClick={handleCopy}>
          {copied ? <Check strokeWidth={2} /> : <Copy strokeWidth={1.75} />}
        </ActionButton>
        {onRegenerate && (
          <ActionButton label={t('Agent.AriaRegenerate')} buttonName="AGENT_MESSAGE_REGENERATE" onClick={onRegenerate}>
            <RefreshCw strokeWidth={1.75} />
          </ActionButton>
        )}
      </div>
      <span className="mt-1 block text-[11px] text-gray-400">{formatTime(timestamp)}</span>

      <FeedbackModal
        open={modal.open}
        mode={modal.mode}
        onOpenChange={(open) => !open && closeModal()}
        onSubmit={handleModalSubmit}
      />
    </>
  );
};

export default MessageActions;
